import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { airconCostCalulator } from '../../../../utils/aircon-cost-calulator'
import { UserContext } from '../../../../context/UserContext'
import { RequestContext } from '../../../../context/RequestContext'
import { GtagContext } from '../../../../pages/_app'
import { getServiceCategorySettingsByCountryAndRequestType } from '../../../../api/request-category/request-category-api'
import { to12HourFormat } from '../../../../utils/time-converter'
import { SystemError } from '../../../error/SystemError'
import { LoadingCover } from '../../../commons/LoadingCover'
import { GtagLoader, DateTimeInput, TimeSlotInput, ServiceSummaryCard, NavigationButtons } from '../../../commons/BookingFormComponents'
import { AlertCircle, CheckCircle, Sparkles, Calendar } from 'lucide-react'
import { clampServiceDate, isServiceDateOutOfRange, toDateStr } from '../../../../utils/service-date'
import { getMarketByName, localiseTerms } from '../../../../utils/markets'
import { formatMoney } from '../../../../utils/format-money'

// "$47.84" for Singapore (unchanged); "AED 119.60" / "RM89.24" elsewhere.
const moneyFor = (countryCd) => (v) => (countryCd === 'sg' ? `$${v}` : formatMoney(v, countryCd))

// Compact Price Card Component - no motion animation to prevent layout shift
// local: UAE / Malaysia wording ("Price", "was", "outdoor units") instead of
// Singapore's "Nett", "U.P." and "Comp.".
const PriceCard = ({ servicingCost, gasCost, originalServiceCost, originalGasCost, gasCount, serviceType, money = moneyFor('sg'), local = false }) => {
    return (
        <div className="bg-linear-to-br from-orange-50 to-amber-50 rounded-xl p-2 border border-orange-100">
            <div className="flex justify-center items-center gap-6 text-center">
                {servicingCost > 0 && (
                    <div>
                        <div className="text-xs text-gray-500 mb-1">
                            {serviceType === "Aircon Repair (est.)" ? "Visit & Troubleshoot" : (local ? "Service price" : "Servicing Nett")}
                        </div>
                        <div className="text-xl font-bold text-gray-800">
                            {money(servicingCost)}
                            <span className="text-xs text-gray-400 font-normal ml-1">{local ? `(was ${money(originalServiceCost)})` : <>(U.P. {money(originalServiceCost)})</>}</span>
                        </div>
                    </div>
                )}
                {gasCost > 0 && (
                    <div>
                        <div className="text-xs text-gray-500 mb-1">{local ? `Gas (${gasCount} outdoor unit${gasCount > 1 ? 's' : ''})` : <>Gas Nett ({gasCount} Comp.)</>}</div>
                        <div className="text-xl font-bold text-gray-800">
                            {money(gasCost)}
                            <span className="text-xs text-gray-400 font-normal ml-1">{local ? `(was ${money(originalGasCost)})` : <>(U.P. {money(originalGasCost)})</>}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Repair Warning Component - no motion animation to prevent layout shift
// market: UAE / Malaysia wording; Singapore passes none.
const RepairWarning = ({ market }) => (
    <div className="p-3 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 text-xs rounded-r-lg">
        <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
                {market
                    ? `Since you selected ${localiseTerms('Aircon Repair', market)}, our technician will visit to diagnose the issue. Service will proceed only with your approval of any extra cost.`
                    : <>Since you selected Aircon Repair, our technician will visit to diagnose the issue. 
                Service will proceed only with your approval of any extra cost.</>}
            </span>
        </div>
    </div>
);

// New User Discount Badge - no motion animation to prevent layout shift
// The "up to 20%" sign-up offer is Singapore's; other markets get a plain note.
const DiscountBadge = ({ percent, market }) => (
    <div className="flex justify-center">
        <div className="inline-flex items-center gap-1.5 px-4 py-2 bg-linear-to-r from-gray-100 to-gray-50 rounded-full">
            <Sparkles className="w-4 h-4 text-orange-500" />
            <span className="text-sm font-semibold text-gray-700">{market ? 'New customer? A first-booking discount applies' : 'New Sign Up? Enjoy Up to 20% Off'}</span>
        </div>
    </div>
);

// Rate Factor Message Component
const RateFactorMessage = ({ factor }) => {
    const messages = {
        late: { text: "Selecting a time during usual business hours may be cheaper", color: "text-red-600" },
        emergency: { text: "For a better price, try a weekday at least 2 days in advance", color: "text-red-600" },
        urgent: { text: "High demand! A further date may be cheaper", color: "text-red-600" },
        sunday: { text: "Next Sunday has high demand! Try a weekday for better price", color: "text-red-600" },
        weekend: { text: "Try a weekday for better price", color: "text-red-600" },
        ph: { text: "Pricing may be higher on public holidays", color: "text-red-600" },
        cl: { text: "We are not operating this day", color: "text-red-600" },
        night: { text: "Try booking for slots after 9am for better pricing", color: "text-red-600" },
        "early-morning": { text: "Try booking for slots after 9am for better pricing", color: "text-red-600" }
    };

    const message = messages[factor];

    if (!factor) {
        return (
            <div className="flex items-center justify-center gap-1 text-xs text-green-600 font-medium">
                <CheckCircle className="w-3 h-3" />
                <span>Cheapest in your locality!</span>
            </div>
        );
    }

    if (!message) return null;

    return (
        <div className={`flex items-center justify-center gap-1 text-xs ${message.color}`}>
            <AlertCircle className="w-3 h-3" />
            <span>{message.text}</span>
        </div>
    );
};

export const AirconBookingDateTime = (props) => {
    
    const goBack = props.goBack
    const goToSection4 = props.goToSection4    
    const countryName = props.countryName
    const countryCurrCd = props.countryCurrCd
    // UAE / Malaysia wording for the notes below; undefined for Singapore.
    const labelMarket = getMarketByName(countryName)?.countryCd !== 'sg' ? getMarketByName(countryName) ?? undefined : undefined

    const serviceHours = 2
    const [ minAllowed, setMinAllowed ] = useState("09:00")
    const [ maxAllowed, setMaxAllowed ] = useState("19:00")
    const [ minDate, setMinDate ] = useState("")
    const [ maxDate, setMaxDate ] = useState("")
    const isInitialLoaded = useRef(false)

    const [ displayServiceDate, setDisplayServiceDate ] = useState("")
    const { isGtagLoaded } = useContext(GtagContext)
    const [ serviceType, setServiceType ] = useState("")
    const [ gasCount, setGasCount ] = useState(1)
    const [ originalServiceCost, setOriginalServiceCost ] = useState(0)
    const [ originalGasCost, setOriginalGasCost ] = useState(0)
    const [ newUserDiscountPercent, setNewUserDiscountPercent ] = useState(0)
    const [ discountPercent, setDiscountPercent ] = useState(0)

    const currentDate = new Date().toLocaleString( 'sv', { timeZoneName: 'short' } ).substring(0,10)

    const [ isLoading, setIsLoading ] = useState(false)
    const [ isSystemError, setIsSystemError ] = useState(false)
    const [ errorContent, setErrorContent ]  = useState({'header': "", 'body': ""}) 

    const { 
        requestCriteria, 
        requestType, 
        serviceDate, 
        setServiceDate,
        startTime,
        setStartTime,
        endTime,
        setEndTime, 
        setServiceCost, 
        serviceSummary, 
        setServiceSummary,
        setTotalDiscountPercent,
        setOriginalCost
    } = useContext(RequestContext);

    const { userId } = useContext(UserContext)

    //AIRCON COST CALCULATION
    const [ servicingCost, setServicingCost ] = useState(0)
    const [ newUserServicingCost, setNewUserServicingCost ] = useState(0)
    const [ gasCost, setGasCost ] = useState(0)
    const [ newUserGasCost, setNewUserGasCost ] = useState(0)
    const [ rateAggregatorFactor, setRateAggregatorFactor ] = useState("")

    const handleServiceDateChange = (e) => {
        // iOS date picker ignores min/max, so clamp the selection into the allowed range
        const selectedDate = clampServiceDate(e.target.value, minDate, maxDate)
        if(!selectedDate) return
        setServiceDate(selectedDate)
        setDisplayServiceDate(new Date(selectedDate).toString().substring(0,16))
        if(selectedDate === currentDate){          
            const min = new Date().getHours() + 1
            setMinAllowed(min + ":00")  
            setMaxAllowed("23:00")         
        } else {
            setMinAllowed("09:00")    
            setMaxAllowed("19:00")
        }
    }

    const handleStartTimeChange = (e) => {
        setStartTime(e.target.value)
    }

    useEffect(() => {
        if (!startTime || !serviceHours) return;
    
        const [minHour, minMinutes] = startTime.split(":").map(Number);
        const serviceDuration = parseFloat(serviceHours);
    
        let hour = minHour + Math.floor(serviceDuration);
        let minutes = minMinutes + (serviceDuration % 1) * 60;
    
        if (minutes >= 60) {
            minutes -= 60;
            hour += 1;
        }
    
        if (hour >= 24) {
            hour -= 24;
        }
    
        const formattedHour = hour.toString().padStart(2, "0");
        const formattedMinutes = minutes.toString().padStart(2, "0");
    
        setEndTime(`${formattedHour}:${formattedMinutes}`);

    }, [startTime, setEndTime, serviceHours]);  

    const submit = (e) => {
        e.preventDefault()

        // Safety net - iOS allows picking a date outside the min/max range
        if(isServiceDateOutOfRange(serviceDate, minDate, maxDate)){
            const clamped = clampServiceDate(serviceDate, minDate, maxDate)
            setServiceDate(clamped)
            setDisplayServiceDate(new Date(clamped).toString().substring(0,16))
            return
        }

        let requestCriteriaW
        requestCriteriaW = 
        {
            'criteria': "Requested service date - " + displayServiceDate,
            [99]: serviceDate
        },
        requestCriteria.push(requestCriteriaW)
        
        requestCriteriaW = 
        {
            'criteria': "Arrival time slot - " + to12HourFormat(startTime) + " to " + to12HourFormat(endTime),
            [100]: to12HourFormat(startTime) + " to " + to12HourFormat(endTime)
        }
        requestCriteria.push(requestCriteriaW)

        setTotalDiscountPercent(newUserDiscountPercent ? (discountPercent + newUserDiscountPercent).toFixed(2) : discountPercent)

        if(servicingCost > 0){
            if(gasCost > 0 ){
                if(newUserDiscountPercent > 0){
                    const cost = (parseFloat(newUserServicingCost) + parseFloat(newUserGasCost)).toFixed(2)
                    setServiceCost(cost)
                } else {
                    const cost = (parseFloat(servicingCost) + parseFloat(gasCost)).toFixed(2)
                    setServiceCost(cost)
                }
            } else {
                if(newUserDiscountPercent > 0){
                    setServiceCost(newUserServicingCost)
                } else {
                    setServiceCost(servicingCost) 
                }
            }
        } else {
            if(gasCost > 0 ){
                if(newUserDiscountPercent > 0){
                    setServiceCost(newUserGasCost)
                } else {
                    setServiceCost(gasCost)
                }
            }
        }    
        
        try {            
            gtag('event', 'conversion', {'send_to': 'AW-823304067/jxrfCOr70YsYEIO_yogD'});
        } catch (error) {
            console.log(error)
        }

        goToSection4()
    }
    
    const acCostCalculatorApi = airconCostCalulator
    (
        !userId ? null : userId,
        requestCriteria, 
        serviceDate, 
        startTime, 
        endTime, 
        setServicingCost, 
        setRateAggregatorFactor, 
        setGasCost, 
        setServiceType, 
        setGasCount, 
        setServiceSummary, 
        setDiscountPercent, 
        setNewUserDiscountPercent,
        setOriginalCost,
        setOriginalServiceCost,
        setOriginalGasCost,
        setNewUserServicingCost,
        setNewUserGasCost,
        setIsLoading,
        setErrorContent,
        setIsSystemError,
        // Singapore omits it (unchanged request); other markets name themselves
        getMarketByName(countryName) && getMarketByName(countryName).countryCd !== 'sg' ? countryName : undefined
    )

    const refresh = useCallback(() => {
        if(isInitialLoaded.current) return
        acCostCalculatorApi()
    }, [isInitialLoaded, acCostCalculatorApi])

    useEffect(() => {
        if(!requestCriteria) return
        if(!isGtagLoaded) return

        if(!isInitialLoaded.current){
            isInitialLoaded.current = true
            acCostCalculatorApi()
        } else {
            acCostCalculatorApi()
        }
    }, [requestCriteria, acCostCalculatorApi, isGtagLoaded])

    useEffect(() => {
        if(!countryName || !requestType) return

        const getServiceCategorySettings = async() => {
            try {
                const serviceSetting = await getServiceCategorySettingsByCountryAndRequestType(countryName, requestType)
                
                const minD = new Date()
                
                if(minD.getHours() > 21) {
                    minD.setDate(minD.getDate() + (serviceSetting.nextSlotAdvance + 1))
                } else {
                    minD.setDate(minD.getDate() + serviceSetting.nextSlotAdvance)
                }

                setMinDate(toDateStr(minD))
                
                const sDate = new Date()  
                sDate.setDate(sDate.getDate() + 2)
                if(minD > sDate){
                    // setDate(minD.getDate()) only copies the day of month and can land
                    // before minD when minD rolls over to the next month
                    sDate.setTime(minD.getTime())
                }

                if(sDate.getDay() === 0){
                    sDate.setDate(sDate.getDate() + 1)
                }

                if(sDate.getDay() === 6){
                    sDate.setDate(sDate.getDate() + 2)
                }

                setServiceDate(toDateStr(sDate))
                setDisplayServiceDate(sDate.toString().substring(0,16))

                const maxD = new Date()
                maxD.setDate(maxD.getDate() + 182)
                setMaxDate(toDateStr(maxD))

                setStartTime("13:00")
                setEndTime("15:00")

            } catch (error) {
                console.log(error)
            }
        }
        
        getServiceCategorySettings();

    }, [setServiceDate, setStartTime, setEndTime, countryName, requestType])

    return (
        <>
            <form className="max-w-xl mx-auto px-3" onSubmit={(e) => submit(e)}>
                {/* Price Display - min-height prevents layout shift when content loads */}
                <div className="min-h-35 mb-4">
                    {/* Loading State */}
                    {!isGtagLoaded && <GtagLoader isLoading={true} />}
                    
                    {/* Price Display */}
                    {isGtagLoaded && (servicingCost > 0 || gasCost > 0) && (
                        <div className="space-y-3">
                            <PriceCard 
                                servicingCost={servicingCost}
                                gasCost={gasCost}
                                originalServiceCost={originalServiceCost}
                                originalGasCost={originalGasCost}
                                gasCount={gasCount}
                                serviceType={serviceType}
                                money={moneyFor(getMarketByName(countryName)?.countryCd ?? 'sg')}
                                local={!!labelMarket}
                            />
                            
                            {/* Repair Warning */}
                            {serviceType === "Aircon Repair (est.)" && <RepairWarning market={labelMarket} />}
                            
                            {/* New User Discount */}
                            {newUserDiscountPercent > 0 && <DiscountBadge percent={newUserDiscountPercent} market={labelMarket} />}
                            
                            {/* Rate Factor Message */}
                            <RateFactorMessage factor={rateAggregatorFactor} />
                        </div>
                    )}
                    
                    {/* Placeholder when gtag loaded but no cost yet */}
                    {isGtagLoaded && servicingCost === 0 && gasCost === 0 && (
                        <div className="text-center text-sm text-gray-400 py-4">
                            Select date and time to see pricing
                        </div>
                    )}
                </div>
                
                {/* Date & Time Selection */}
                <div className="space-y-5 mb-4">
                    <DateTimeInput
                        type="date"
                        value={serviceDate}
                        min={minDate}
                        max={maxDate}
                        onChange={(e) => handleServiceDateChange(e)}
                        label="Service Date"
                        icon={Calendar}
                        disabled={!minDate}
                    />
                    
                    <TimeSlotInput
                        startTime={startTime}
                        endTime={endTime}
                        onStartTimeChange={(e) => handleStartTimeChange(e)}
                        minAllowed={minAllowed}
                        maxAllowed={maxAllowed}
                        serviceHours={serviceHours}
                        variant="arrival"
                    />
                </div>
                
                {/* Service Summary */}
                <div className='min-h-24'>
                    <ServiceSummaryCard summary={serviceSummary} />
                </div>
                
                {/* Navigation Buttons */}
                <div className='min-h-24'>
                    <NavigationButtons
                        onBack={() => goBack()}
                        showNext={rateAggregatorFactor !== "cl" && !isServiceDateOutOfRange(serviceDate, minDate, maxDate)}
                        promoText={newUserDiscountPercent > 0 && !labelMarket ? "Upto 20% OFF" : null}
                    />
                </div>
            </form>
            
            {/* Scripts removed - now loaded globally in _app.js */}
            {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
            {isLoading && <LoadingCover/>}
        </>
    )
}
